"use client";

import { useState } from "react";
import { MainLayout } from "@/components/layout/main-layout";
import { RouteGuard } from "@/components/auth/route-guard";
import { Button } from "@/components/ui/button";
import { apiPlaygroundExamples } from "@/lib/mock-data";
import { Code, Play, Key } from "lucide-react";
import { useAuth } from "@/components/providers/auth-context";

export default function PlaygroundPage() {
  const { getIdToken, user } = useAuth();
  const [code, setCode] = useState(apiPlaygroundExamples[0].code);
  const [response, setResponse] = useState<string>("");
  const [apiKey, setApiKey] = useState<string>("");
  const [generating, setGenerating] = useState(false);
  const generateApiKey = async () => {
    if (!user) {
      setResponse("Please sign in to generate an API key");
      return;
    }

    try {
      setGenerating(true);
      const token = await getIdToken();
      const response = await fetch('/api/api-keys', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ projectName: 'API Playground' })
      });

      if (!response.ok) {
        throw new Error('Failed to generate API key');
      }

      const data = await response.json();
      if (data.key?.key) {
        setApiKey(data.key.key);
      } else {
        throw new Error('Invalid API key format received');
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setResponse(`Error generating API key: ${errorMessage}`);
    } finally {
      setGenerating(false);
    }
  };

  const executeCode = () => {
    let originalConsoleLog: typeof console.log | undefined;
    
    try {
      // Create a new function with captured console.log
      let output = "";
      originalConsoleLog = console.log;
      console.log = (...args) => {
        output += args.map(arg => 
          typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
        ).join(" ") + "\n";
      };

      // If code contains SDK-related code, show message
      if (code.includes("Docs4AI") || code.includes("docs4ai")) {
        setResponse("API/SDK calls require backend execution. Coming soon!");
        return;
      }

      // Add safety wrapper around the code
      const safeCode = `
        "use strict";
        const setTimeout = undefined;
        const setInterval = undefined;
        const clearTimeout = undefined;
        const clearInterval = undefined;
        const fetch = undefined;
        const window = undefined;
        const document = undefined;
        ${code}
      `;

      // Execute the code and capture return value
      const result = new Function(safeCode)();
      
      // Set response
      setResponse(output + (result !== undefined ? "\nReturn value: " + result : ""));
    } catch (error: unknown) {
      if (error instanceof Error) {
        setResponse(`Error: ${error.message}`);
      } else {
        setResponse(`Error: ${String(error)}`);
      }
    } finally {
      // Always restore console.log
      if (originalConsoleLog) {
        console.log = originalConsoleLog;
      }
    }
  };

  return (
    <MainLayout>
      <RouteGuard>
      <section className="py-12 md:py-20 bg-background">
        {/* Removed max-w-6xl */}
        <div className="container px-4 md:px-6 mx-auto">
          <div className="flex flex-col items-center justify-center space-y-4 text-center max-w-3xl mx-auto">
            <div className="space-y-2">
              <h1 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">
                API Playground
              </h1>
              <p className="max-w-[700px] text-muted-foreground md:text-xl">
                Test and experiment with the Docs4AI API
              </p>
            </div>
          </div>

          {/* Removed max-w-7xl */}
          <div className="mt-12 flex flex-col gap-8 mx-auto">
            <div className="grid gap-8 md:grid-cols-[1fr_300px]">
              {/* Code Editor */}
              <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Code className="h-5 w-5" />
                  <h2 className="text-xl font-semibold">Code Editor</h2>
                </div>
                <Button onClick={executeCode}>
                  <Play className="mr-2 h-4 w-4" />
                  Run
                </Button>
              </div>
              <div className="relative min-h-[500px] rounded-lg border bg-black p-4 text-white">
                <textarea
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="w-full h-full min-h-[500px] bg-transparent text-white font-mono text-sm focus:outline-none resize-none"
                  spellCheck="false"
                />
              </div>
              </div>

              {/* Sidebar */}
              <div className="flex flex-col gap-6">
                {/* Examples */}
                <div className="rounded-lg border bg-card p-4">
                  <h3 className="mb-4 text-lg font-medium">Examples</h3>
                  <div className="flex flex-col gap-2">
                    {apiPlaygroundExamples.map((example, index) => (
                      <Button
                        key={index}
                        variant="outline"
                        className="justify-start"
                        onClick={() => setCode(example.code)}
                      >
                        {example.name}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Configuration */}
                <div className="rounded-lg border bg-card p-4">
                  <h3 className="mb-4 text-lg font-medium">Configuration</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label
                          htmlFor="api-key"
                          className="block text-sm font-medium"
                        >
                          API Key
                        </label>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={generateApiKey}
                          disabled={generating || !user}
                        >
                          <Key className="mr-2 h-4 w-4" />
                          Generate Key
                        </Button>
                      </div>
                      <input
                        id="api-key"
                        type="text"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        placeholder={user ? "Generate an API key" : "Please sign in to generate API key"}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="model"
                        className="mb-2 block text-sm font-medium"
                      >
                        Model
                      </label>
                      <select
                        id="model"
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <option value="gemini-flash">Gemini Flash</option>
                        <option value="claude-sonnet">Claude 3.7 Sonnet</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Response Section - Now below the editor */}
            <div className="rounded-lg border bg-card p-4 mt-6">
              <h3 className="mb-4 text-lg font-medium">Response</h3>
              <div className="rounded-md bg-muted p-3 text-sm font-mono whitespace-pre-wrap min-h-[100px] max-h-[300px] overflow-y-auto">
                {response || "Run the code to see the response"}
              </div>
            </div>
          </div>
        </div>
      </section>
      </RouteGuard>
    </MainLayout>
  );
}
